import axios from 'axios';
import { toast } from 'sonner';

// Mobile-aware blob download helper. iOS Safari + some Android browsers ignore
// the `download` attribute on cross-origin or blob URLs, so we fall back to
// opening the blob in a new tab (the OS-native share/save UI then takes over).
function _isMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '');
}

function _triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  try {
    // Web Share API path — premium on modern mobile (iOS 16+, Android Chrome).
    // Lets the user save to Files / share to Instagram / etc.
    if (_isMobileDevice() && typeof navigator !== 'undefined' && navigator.canShare) {
      try {
        const file = new File([blob], filename, { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: filename }).catch(() => {});
          // Don't return — still offer the local link as a fallback in case share is dismissed.
        }
      } catch (_) { /* ignore — falls through to anchor click */ }
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.rel = 'noopener';
    a.target = '_blank';  // mobile fallback: open in tab if download is blocked
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    // Give the browser a moment to start the download before revoking
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }
}

export async function downloadRecipeExport({ API, getHeaders, id, ids, format = 'xlsx', variant, scope = 'admin' }) {
  try {
    const base = scope === 'manager' ? API : API;    // same base — scope only affects which endpoint the backend chooses via the auth role
    let res;
    if (Array.isArray(ids) && ids.length > 0) {
      // Bulk export only supports xlsx/pdf (no per-recipe JPG bundle yet)
      res = await axios.post(
        `${base}/recipes/export`,
        { ids, format },
        { headers: getHeaders(), responseType: 'blob' },
      );
    } else if (id) {
      // Premium single-recipe routes: /recipes/{id}/export.pdf and /recipes/{id}/export.jpg
      let url;
      if (format === 'jpg') {
        const v = variant || 'social';
        url = `${base}/recipes/${id}/export.jpg?variant=${v}`;
      } else if (format === 'pdf') {
        url = `${base}/recipes/${id}/export.pdf`;
      } else {
        url = `${base}/recipes/${id}/export?format=${format}`;
      }
      res = await axios.get(url, { headers: getHeaders(), responseType: 'blob' });
    } else {
      throw new Error('No recipe id(s)');
    }

    // Derive filename from header (fallback to recipe_{ts}.{ext})
    const disp = res.headers['content-disposition'] || '';
    const match = /filename="?([^"]+)"?/.exec(disp);
    const extMap = { pdf: 'pdf', jpg: 'jpg', xlsx: 'xlsx' };
    const ext = extMap[format] || 'xlsx';
    const filename = match ? match[1] : `recipe_${Date.now()}.${ext}`;

    const mimeMap = {
      pdf: 'application/pdf',
      jpg: 'image/jpeg',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    };
    const blob = new Blob([res.data], { type: mimeMap[format] || mimeMap.xlsx });
    _triggerDownload(blob, filename);

    const label = format === 'jpg'
      ? (variant === 'full' ? 'Full recipe JPG' : 'Instagram card')
      : format.toUpperCase();
    toast.success(`Exported ${label}`);
  } catch (err) {
    // Axios with responseType=blob may return Blob in err.response.data for error responses.
    let detail;
    try {
      const data = err?.response?.data;
      if (data instanceof Blob) {
        const text = await data.text();
        try { detail = JSON.parse(text)?.detail; } catch (_) { detail = text; }
      } else {
        detail = data?.detail;
      }
    } catch (_) { /* swallow */ }
    toast.error(detail || err?.message || 'Export failed');
  }
}
